import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";

export default function Navbar() {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isGuidesOpen, setIsGuidesOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileCategoriesOpen, setIsMobileCategoriesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const guidesRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const guidesTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    function handleScroll() {
      setIsScrolled(window.scrollY > 60);
    }
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
      if (guidesRef.current && !guidesRef.current.contains(event.target as Node)) {
        setIsGuidesOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleMouseEnter = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
    setIsDropdownOpen(true);
  };

  const handleMouseLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setIsDropdownOpen(false);
    }, 200);
  };

  const handleGuidesEnter = () => {
    if (guidesTimerRef.current) {
      clearTimeout(guidesTimerRef.current);
      guidesTimerRef.current = null;
    }
    setIsGuidesOpen(true);
  };

  const handleGuidesLeave = () => {
    guidesTimerRef.current = setTimeout(() => {
      setIsGuidesOpen(false);
    }, 200);
  };

  const navBgClass = isScrolled
    ? "bg-white shadow-sm border-b border-background-200/60"
    : "bg-transparent border-b border-transparent";

  const navTextClass = isScrolled
    ? "text-foreground-800 hover:text-foreground-950"
    : "text-white/90 hover:text-white";

  const logoClass = isScrolled
    ? "text-foreground-950"
    : "text-white";

  const mobileBtnClass = isScrolled
    ? "text-foreground-800 hover:bg-background-100"
    : "text-white hover:bg-white/15";

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${navBgClass}`}>
      <div className="w-full px-4 md:px-6">
        <div className="flex items-center justify-between h-16">
          {/* Left: Nav links */}
          <div className="hidden md:flex items-center gap-8">
            <Link
              to="/"
              className={`text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}
            >
              Home
            </Link>

            {/* Categories Dropdown */}
            <div
              ref={dropdownRef}
              className="relative"
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              <button
                className={`flex items-center gap-1 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              >
                Categories
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isDropdownOpen ? "rotate-180" : ""}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {isDropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-background-200/70 rounded-xl py-2 shadow-xl max-h-[420px] overflow-y-auto z-50">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <span className="font-medium">{cat.name}</span>
                      <span className="block text-xs text-foreground-400 mt-0.5">{cat.description}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Shopping Guide Dropdown */}
            <div
              ref={guidesRef}
              className="relative"
              onMouseEnter={handleGuidesEnter}
              onMouseLeave={handleGuidesLeave}
            >
              <button
                className={`flex items-center gap-1 text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ${navTextClass}`}
                onClick={() => setIsGuidesOpen(!isGuidesOpen)}
              >
                Shopping Guide
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isGuidesOpen ? "rotate-180" : ""}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {isGuidesOpen && (
                <div className="absolute top-full left-0 mt-2 w-72 bg-white border border-background-200/70 rounded-xl py-2 shadow-xl max-h-[360px] overflow-y-auto z-50">
                  {articles.map((article) => (
                    <Link
                      key={article.slug}
                      to={`/article/${article.slug}`}
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-foreground-950 transition-colors cursor-pointer"
                    >
                      <span className="font-medium line-clamp-1">{article.title}</span>
                      <span className="block text-xs text-foreground-400 mt-0.5">{article.readTime}</span>
                    </Link>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Center: Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer">
            <span className={`font-heading text-lg md:text-xl font-bold transition-colors duration-300 whitespace-nowrap ${logoClass}`}>
              Superbuy Spreadsheet
            </span>
          </Link>

          {/* Right: Actions */}
          <div className="flex items-center gap-3">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className={`hidden md:inline-flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-full transition-all whitespace-nowrap cursor-pointer ${
                isScrolled
                  ? "bg-foreground-950 text-white hover:bg-foreground-800"
                  : "bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm"
              }`}
            >
              Visit Main Site
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>

            {/* Mobile menu button */}
            <button
              className={`md:hidden w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${mobileBtnClass}`}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <i className={`text-lg ${isMobileMenuOpen ? "ri-close-line" : "ri-menu-line"}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-white/15 py-3 flex flex-col gap-1 bg-white/95 backdrop-blur-md rounded-b-xl shadow-lg mx-[-16px] px-4">
            <Link
              to="/"
              className="px-3 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg transition-colors cursor-pointer flex items-center gap-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span className="w-5 h-5 flex items-center justify-center text-accent-600">
                <i className="ri-home-4-line"></i>
              </span>
              Home
            </Link>

            {/* Mobile Categories */}
            <div>
              <button
                className="w-full px-3 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-100 rounded-lg transition-colors cursor-pointer flex items-center justify-between"
                onClick={() => setIsMobileCategoriesOpen(!isMobileCategoriesOpen)}
              >
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 flex items-center justify-center text-accent-600">
                    <i className="ri-grid-line"></i>
                  </span>
                  Categories
                </span>
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${isMobileCategoriesOpen ? "rotate-180" : ""}`}>
                  <i className="ri-arrow-down-s-line text-sm"></i>
                </span>
              </button>
              {isMobileCategoriesOpen && (
                <div className="ml-3 mt-1 space-y-0.5 border-l-2 border-accent-200 pl-3 max-h-[320px] overflow-y-auto">
                  {categories.map((cat) => (
                    <a
                      key={cat.slug}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      <span className="font-medium">{cat.name}</span>
                      <span className="block text-xs text-foreground-400">{cat.description}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Shopping Guide */}
            <div>
              <p className="px-3 py-2 text-xs font-semibold uppercase tracking-wider text-foreground-400">Shopping Guide</p>
              <div className="max-h-[280px] overflow-y-auto">
                {articles.slice(0, 13).map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="block px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors cursor-pointer"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    <span className="font-medium line-clamp-1">{article.title}</span>
                    <span className="block text-xs text-foreground-400">{article.readTime}</span>
                  </Link>
                ))}
              </div>
            </div>

            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="mx-3 mt-2 px-5 py-3 bg-foreground-950 text-white text-sm font-semibold rounded-full text-center hover:bg-foreground-800 transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center gap-2"
              onClick={() => setIsMobileMenuOpen(false)}
            >
              Visit Main Site
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-up-line text-xs"></i>
              </span>
            </a>
          </div>
        )}
      </div>
    </nav>
  );
}